import { Button, Tabs } from 'antd';
import { Plus } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { PageHeader } from '../../../shared/components/PageHeader';
import { sectionTrail } from '../../../shared/components/section-trail';
import { AdapterCatalogue } from '../components/AdapterCatalogue';
import { DeviceMappingSheet } from '../components/DeviceMappingSheet';
import { DeviceMappingsTable } from '../components/DeviceMappingsTable';
import { InstalledIntegrationsTable } from '../components/InstalledIntegrationsTable';
import { IntegrationSheet } from '../components/IntegrationSheet';
import { useAdapterCatalogue } from '../hooks/use-adapter-catalogue';
import { INTEGRATION_TAB_LABELS, integrationTabPath, NEW_DEVICE_MAPPING_PATH, type IntegrationTab } from '../integrations.paths';

interface IntegrationsPageProps {
  tab: IntegrationTab;
  /** Which sheet this route opens over the tab. */
  sheet?: 'install' | 'edit' | 'map';
}

/** Integrations → GPS: the catalogue of built-in adapters, what is installed, and which device is on which truck. */
export function IntegrationsPage({ tab, sheet }: IntegrationsPageProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const catalogue = useAdapterCatalogue();
  const closeSheet = () => navigate(integrationTabPath(tab));

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('integrations')}
        title="Integrations"
        description="GPS vendors ship with the app. Install one with your company's credentials, then say which device is on which truck."
        actions={
          tab === 'device-mappings' && (
            <Button type="primary" icon={<Plus className="size-4" />} onClick={() => navigate(NEW_DEVICE_MAPPING_PATH)}>
              Map a device
            </Button>
          )
        }
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(integrationTabPath(key as IntegrationTab))}
        items={[
          { key: 'catalogue', label: INTEGRATION_TAB_LABELS.catalogue, children: <AdapterCatalogue catalogue={catalogue} /> },
          {
            key: 'installed',
            label: INTEGRATION_TAB_LABELS.installed,
            children: <InstalledIntegrationsTable adapters={catalogue.status === 'loaded' ? catalogue.adapters : []} />,
          },
          { key: 'device-mappings', label: INTEGRATION_TAB_LABELS['device-mappings'], children: <DeviceMappingsTable /> },
        ]}
      />
      {sheet === 'install' && <IntegrationSheet adapterKey={searchParams.get('adapter') ?? undefined} integrationId={undefined} onClose={closeSheet} />}
      {sheet === 'edit' && <IntegrationSheet adapterKey={undefined} integrationId={id} onClose={closeSheet} />}
      {sheet === 'map' && <DeviceMappingSheet onClose={closeSheet} />}
    </>
  );
}
